import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Select, MenuItem, FormControl, InputLabel, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert, TextField,
    Grid
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { ja } from 'date-fns/locale';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import {
    fetchOrganizations,
    fetchClipsByOrganization,
    fetchAuthorsByOrganization,
    createClip,
    updateClip, // 1. 导入 updateClip 函数
    deleteClip
} from '../api/apiService';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit'; // 2. 导入编辑图标

// Helper Maps and initial state... (保持不变)
const typeMap = { 1: 'B站视频', 2: '录播站', 3: '本地源', 4: '直播中', 5: '已下播', 0: '待解析' };
const liveFormatMap = { 1: '3D', 2: '2D', 3: '电台', 4: '联动', 5: '双播', 6: '其它' };
const liveContentMap = { 1: '才艺', 2: '杂谈', 3: '游戏', 4: '综艺', 5: '活动', 6: '观影', 7: '特殊', 8: '投稿', 9: '歌回', 10: '其它' };
const initialFormState = {
    id: null, bv: '', title: '', authorId: '', cover: '', datetime: new Date(),
    type: 1, live_format: 6, live_content: 10, part: 1,
};

const ClipsPage = () => {
    // ... (保留大部分 state)
    const [organizations, setOrganizations] = useState([]);
    const [selectedOrg, setSelectedOrg] = useState('');
    const [clips, setClips] = useState([]);
    const [authors, setAuthors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
    const [totalRows, setTotalRows] = useState(0);

    // --- 3. 修改 state 以支持新增和编辑模式 ---
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState('add'); // 'add' or 'edit'
    const [formData, setFormData] = useState(initialFormState);

    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewPayload, setPreviewPayload] = useState(null);
    const [submissionStatus, setSubmissionStatus] = useState({ msg: '', type: '' });
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [clipToDelete, setClipToDelete] = useState(null);


    // ... (保留所有 helper 和 handler 函数)
    const formatLocalDateTime = (date) => {
        if (!date) return '';
        const pad = (num) => num.toString().padStart(2, '0');
        const year = date.getFullYear();
        const month = pad(date.getMonth() + 1);
        const day = pad(date.getDate());
        const hours = pad(date.getHours());
        const minutes = pad(date.getMinutes());
        const seconds = pad(date.getSeconds());
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    };

    const extractBvId = (url) => {
        if (typeof url !== 'string' || !url) return '';
        const match = url.match(/BV[1-9A-HJ-NP-Za-km-z]{10}/);
        return match ? match[0] : '';
    };

    useEffect(() => {
        const fetchOrgs = async () => {
            try {
                const data = await fetchOrganizations();
                setOrganizations(data);
                if (data.length > 0) setSelectedOrg(data[0].id);
            } catch (err) {
                setError('无法加载社团列表');
            }
        };
        fetchOrgs();
    }, []);

    const refreshData = useCallback(async () => {
        if (!selectedOrg) return;
        setLoading(true);
        setError('');
        try {
            const [clipsResponse, authorsData] = await Promise.all([
                fetchClipsByOrganization(selectedOrg, {
                    page: paginationModel.page + 1,
                    size: paginationModel.pageSize
                }),
                fetchAuthorsByOrganization(selectedOrg)
            ]);
            setClips(clipsResponse.clips || []);
            setTotalRows(clipsResponse.total || 0);
            setAuthors(authorsData);
        } catch (err) {
            setError(`加载数据失败`);
            setClips([]);
            setTotalRows(0);
            setAuthors([]);
        } finally {
            setLoading(false);
        }
    }, [selectedOrg, paginationModel]);

    useEffect(() => {
        refreshData();
    }, [refreshData]);

    useEffect(() => {
        setPaginationModel({ page: 0, pageSize: 20 });
    }, [selectedOrg]);

    // --- 4. 修改弹窗打开处理函数 ---
    const handleOpenAddDialog = () => {
        const defaultDate = new Date();
        defaultDate.setSeconds(0, 0);
        setDialogMode('add');
        setSubmissionStatus({ msg: '', type: '' });
        setFormData({
            ...initialFormState,
            datetime: defaultDate,
            authorId: authors.length > 0 ? authors[0].id : '',
        });
        setDialogOpen(true);
    };

    const handleOpenEditDialog = (row) => {
        setDialogMode('edit');
        setSubmissionStatus({ msg: '', type: '' });
        setFormData({
            id: row.id,
            bv: row.bv || (row.type === 1 ? extractBvId(row.redirectUrl) : ''),
            title: row.title,
            authorId: row.author.id,
            cover: row.cover,
            datetime: new Date(row.datetime),
            type: row.type,
            live_format: row.live_format,
            live_content: row.live_content,
            part: row.part,
        });
        setDialogOpen(true);
    };


    const handleFormChange = (event) => {
        const { name, value } = event.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };
    
    const handleDateChange = (newDate) => {
        if (newDate instanceof Date && !isNaN(newDate)) {
            newDate.setSeconds(0, 0);
        }
        setFormData(prev => ({ ...prev, datetime: newDate }));
    };

    const handlePreview = () => {
        if (!formData.title || !formData.cover || (formData.type === 1 && !formData.bv) || !formData.authorId) {
            alert("请填写所有必填项 (作者, 标题, 封面, 以及B站视频对应的BV号)！");
            return;
        }
        const payload = {
            authorId: formData.authorId,
            bv: formData.type === 1 ? formData.bv : "",
            title: formData.title,
            datetime: formatLocalDateTime(new Date(formData.datetime)),
            cover: formData.cover,
            part: parseInt(formData.part, 10) || 1,
            type: formData.type,
            live_format: formData.live_format,
            live_content: formData.live_content
        };
        setPreviewPayload(payload);
        setDialogOpen(false);
        setPreviewOpen(true);
    };

    // --- 5. 修改确认上传/更新的函数 ---
    const handleConfirmSubmit = async () => {
        const action = dialogMode === 'add' ? createClip : updateClip;
        const successMessage = dialogMode === 'add' ? '新增成功！' : '更新成功！';
        const failMessage = dialogMode === 'add' ? '新增失败' : '更新失败';
        
        setSubmissionStatus({ msg: '提交中...', type: 'info' });
        try {
            if (dialogMode === 'add') {
                await action(previewPayload);
            } else {
                await action(formData.id, previewPayload);
            }
            setSubmissionStatus({ msg: successMessage, type: 'success' });
            setTimeout(() => {
                setPreviewOpen(false);
                refreshData();
            }, 1500);
        } catch (err) {
            setSubmissionStatus({ msg: `${failMessage}: ${err.message}`, type: 'error' });
        }
    };
    
    const handleOpenDeleteDialog = (id) => {
        setClipToDelete(id);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        try {
            await deleteClip(clipToDelete);
            setDeleteDialogOpen(false);
            setClipToDelete(null);
            refreshData();
        } catch (err) {
            alert(`删除失败: ${err.message}`);
        }
    };


    const columns = [
        { field: 'id', headerName: 'ID', width: 80 },
        { field: 'bv', headerName: 'BV号', width: 130, valueGetter: (value, row) => row.bv || (row.type === 1 ? extractBvId(row.redirectUrl) : '') },
        { field: 'title', headerName: '标题', flex: 1, minWidth: 200 },
        { field: 'authorId', headerName: '作者', width: 150, valueFormatter: (value, row) => row.author?.name || '' },
        { field: 'cover', headerName: '封面链接', width: 200 },
        { field: 'datetime', headerName: '直播时间', width: 180, type: 'dateTime', valueGetter: (value) => value && new Date(value) },
        { field: 'type', headerName: '类型', width: 100, valueFormatter: (value) => typeMap[value] || '' },
        { field: 'live_format', headerName: '直播形式', width: 100, valueFormatter: (value) => liveFormatMap[value] || '' },
        { field: 'live_content', headerName: '直播内容', width: 100, valueFormatter: (value) => liveContentMap[value] || '' },
        { field: 'part', headerName: 'Part', width: 80 },
        // --- 6. 在操作列中添加编辑按钮 ---
        {
            field: 'actions',
            type: 'actions',
            headerName: '操作',
            width: 100,
            getActions: ({ row }) => {
                return [
                    <GridActionsCellItem
                        icon={<EditIcon />}
                        label="Edit"
                        onClick={() => handleOpenEditDialog(row)}
                    />,
                    <GridActionsCellItem
                        icon={<DeleteIcon />}
                        label="Delete"
                        onClick={() => handleOpenDeleteDialog(row.id)}
                    />,
                ];
            },
        },
    ];

    return (
        <Box sx={{ p: 3, height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h4" gutterBottom>直播信息管理</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <FormControl sx={{ minWidth: 240 }}>
                    <InputLabel id="org-select-label">选择社团</InputLabel>
                    <Select labelId="org-select-label" value={selectedOrg} label="选择社团" onChange={(e) => setSelectedOrg(e.target.value)}>
                        {organizations.map((org) => <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>)}
                    </Select>
                </FormControl>
                <Button color="primary" startIcon={<AddIcon />} variant="contained" onClick={handleOpenAddDialog}>
                    新增直播
                </Button>
            </Box>

            {loading ? <CircularProgress sx={{ m: 'auto' }} /> : error ? <Typography color="error">{error}</Typography> : (
                <Box sx={{ flexGrow: 1, width: '100%' }}>
                    <DataGrid
                        rows={clips}
                        columns={columns}
                        loading={loading}
                        paginationMode="server"
                        rowCount={totalRows}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        pageSizeOptions={[10, 20, 50]}
                    />
                </Box>
            )}
            
            {/* --- 7. 修改 Dialog 以支持两种模式 --- */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="lg">
                <DialogTitle>{dialogMode === 'add' ? '新增' : '编辑'}直播场次</DialogTitle>
                <DialogContent>
                    <Box component="form" noValidate autoComplete="off" sx={{ pt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={3}><FormControl fullWidth required><InputLabel>作者</InputLabel><Select name="authorId" value={formData.authorId} label="作者" onChange={handleFormChange}>{authors.map(a => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}</Select></FormControl></Grid>
                            <Grid item xs={12} sm={3}><FormControl fullWidth required><InputLabel>类型</InputLabel><Select name="type" value={formData.type} label="类型" onChange={handleFormChange}>{Object.entries(typeMap).map(([id, text]) => <MenuItem key={id} value={parseInt(id)}>{text}</MenuItem>)}</Select></FormControl></Grid>
                            <Grid item xs={12} sm={6}><TextField name="title" label="标题" value={formData.title} onChange={handleFormChange} fullWidth required /></Grid>
                            <Grid item xs={12} sm={3}><TextField name="bv" label="BV号" value={formData.bv} onChange={handleFormChange} fullWidth disabled={formData.type !== 1} required={formData.type === 1} helperText={formData.type === 1 ? 'B站视频必填' : ' '} /></Grid>
                            <Grid item xs={12} sm={2}><TextField name="part" label="Part" type="number" value={formData.part} onChange={handleFormChange} fullWidth /></Grid>
                            <Grid item xs={12} sm={7}><TextField name="cover" label="封面链接 (不含 https://)" value={formData.cover} onChange={handleFormChange} fullWidth required /></Grid>
                            <Grid item xs={12} sm={4}><FormControl fullWidth required><InputLabel>直播形式</InputLabel><Select name="live_format" value={formData.live_format} label="直播形式" onChange={handleFormChange}>{Object.entries(liveFormatMap).map(([id, text]) => <MenuItem key={id} value={parseInt(id)}>{text}</MenuItem>)}</Select></FormControl></Grid>
                            <Grid item xs={12} sm={4}><FormControl fullWidth required><InputLabel>直播内容</InputLabel><Select name="live_content" value={formData.live_content} label="直播内容" onChange={handleFormChange}>{Object.entries(liveContentMap).map(([id, text]) => <MenuItem key={id} value={parseInt(id)}>{text}</MenuItem>)}</Select></FormControl></Grid>
                            <Grid item xs={12} sm={4}><LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}><DateTimePicker label="直播时间" value={formData.datetime} onChange={handleDateChange} sx={{ width: '100%' }} views={['year', 'month', 'day', 'hours', 'minutes']} ampm={false} /></LocalizationProvider></Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: '16px 24px' }}>
                    <Button onClick={() => setDialogOpen(false)}>取消</Button>
                    <Button onClick={handlePreview} variant="contained">预览并保存</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>确认提交信息</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>请检查以下将要提交的数据：</Typography>
                    <Box component="pre" sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1, overflowX: 'auto' }}>{JSON.stringify(previewPayload, null, 2)}</Box>
                    {submissionStatus.msg && <Alert severity={submissionStatus.type} sx={{ mt: 2 }}>{submissionStatus.msg}</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setPreviewOpen(false); setDialogOpen(true); }}>返回修改</Button>
                    <Button onClick={handleConfirmSubmit} variant="contained">确认提交</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>确认删除</DialogTitle>
                <DialogContent><Typography>您确定要删除这个直播场次吗？此操作不可撤销。</Typography></DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>取消</Button>
                    <Button onClick={handleConfirmDelete} color="error">确认删除</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ClipsPage;
